import { useRef, useCallback, createContext, useContext, forwardRef, useImperativeHandle, useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useSession } from "../auth/session"
import Api from "../api/api"
import { CodeValueSuggestion, GRANTABLE_LICENSE_TYPES, LicenseType, licenseTypeLabels, SkuVersion } from "../api/models"
import Spinner from "./widgets/Spinner"
import { RootState } from "../redux/store"
import { backdropDismiss } from "../utils/backdropDismiss"

// versions = the sku's releases (newest first, from the caller's page state) —
// codes grant a version, latest by default. onGenerated fires once a batch
// was successfully minted — callers refresh their batch lists with it.
// options.ilokAvailable: the sku's org has iLok switched on, so the dialog
// offers the per-batch "allow iLok" toggle
export interface GenerateCodesOptions {
  ilokAvailable: boolean
}
type OpenGenerateCodes = (skuId: string, versions: SkuVersion[], onGenerated?: () => void, options?: GenerateCodesOptions) => void
const GenerateCodesModalContext = createContext<OpenGenerateCodes>(() => { })

export function useGenerateCodesModal() {
  return useContext(GenerateCodesModalContext)
}

export function GenerateCodesModalProvider({ children }: { children: React.ReactNode }) {
  const dialogRef = useRef<GenerateCodesDialogHandle>(null)

  const open = useCallback<OpenGenerateCodes>((skuId, versions, onGenerated, options) => {
    dialogRef.current?.open(skuId, versions, onGenerated, options)
  }, [])

  return (
    <GenerateCodesModalContext.Provider value={open}>
      {children}
      <GenerateCodesDialog ref={dialogRef} />
    </GenerateCodesModalContext.Provider>
  )
}

interface GenerateCodesDialogHandle {
  open: OpenGenerateCodes
}

const MAX_COUNT = 1000

type Step = "form" | "generating" | "success" | "error"

const GenerateCodesDialog = forwardRef<GenerateCodesDialogHandle>((_, ref) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const onGeneratedRef = useRef<(() => void) | undefined>(undefined)
  const [skuId, setSkuId] = useState<string | undefined>(undefined)
  const [versions, setVersions] = useState<SkuVersion[]>([])
  // "" = the latest version (the default; no skuVersionId sent)
  const [versionId, setVersionId] = useState("")
  const [countText, setCountText] = useState("1")
  const [expiryText, setExpiryText] = useState("")
  const [licenseExpiryText, setLicenseExpiryText] = useState("")
  const [pointsText, setPointsText] = useState("")
  const [licenseType, setLicenseType] = useState(LicenseType.Standard)
  // the org's iLok switch (shows the toggle) and the toggle itself: whether
  // redeemed entitlements may get an iLok counterpart
  const [ilokAvailable, setIlokAvailable] = useState(false)
  const [ilokEnabled, setIlokEnabled] = useState(true)
  // what one code is worth, in dollars as typed; prefilled from the server's
  // suggestion once it arrives (undefined = not fetched yet / failed — the
  // server then seeds the batch itself)
  const [valueText, setValueText] = useState("")
  const [suggestion, setSuggestion] = useState<CodeValueSuggestion | undefined>(undefined)
  // bumps on every open so the suggestion refetches even for the same sku
  const [openSeq, setOpenSeq] = useState(0)
  const [commentText, setCommentText] = useState("")
  const [step, setStep] = useState<Step>("form")
  const [codes, setCodes] = useState<string[]>([])
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const skuName = useSelector((state: RootState) =>
    skuId ? state.skus.entities[skuId]?.data?.name : undefined
  )
  const session = useSession()
  const accessToken = session.accessToken ?? ""

  useImperativeHandle(ref, () => ({
    open: (id, skuVersions, onGenerated, options) => {
      onGeneratedRef.current = onGenerated
      setSkuId(id)
      setVersions(skuVersions)
      setVersionId("")
      setCountText("1")
      setExpiryText("")
      setLicenseExpiryText("")
      setPointsText("")
      setLicenseType(LicenseType.Standard)
      setIlokAvailable(options?.ilokAvailable ?? false)
      setIlokEnabled(true)
      setValueText("")
      setSuggestion(undefined)
      setOpenSeq((n) => n + 1)
      setCommentText("")
      setStep("form")
      setCodes([])
      setErrorMessage(undefined)
      dialogRef.current?.showModal()
    },
  }), [])

  const close = () => dialogRef.current?.close()

  // the suggested value per code, fetched once per opening; a failure leaves
  // the field empty and the backend applies the same suggestion on its own
  useEffect(() => {
    if (!skuId || openSeq === 0) return
    let cancelled = false
    Api.getCodeValueSuggestion({ skuId, accessToken })
      .then((s) => {
        if (cancelled) return
        setSuggestion(s)
        setValueText((s.suggestedValueCents / 100).toFixed(2))
      })
      .catch((err) => console.error("Failed to load the code value suggestion:", err))
    return () => { cancelled = true }
  }, [openSeq, skuId, accessToken])

  const countNum = countText === "" ? NaN : Number(countText)
  const expiryNum = expiryText === "" ? null : Number(expiryText)
  const licenseExpiryNum = licenseExpiryText === "" ? null : Number(licenseExpiryText)
  const pointsNum = pointsText === "" ? null : Number(pointsText)
  const countValid = Number.isInteger(countNum) && countNum >= 1 && countNum <= MAX_COUNT
  const expiryValid = expiryNum === null || (Number.isInteger(expiryNum) && expiryNum >= 1)
  const licenseExpiryValid = licenseExpiryNum === null || (Number.isInteger(licenseExpiryNum) && licenseExpiryNum >= 1)
  const pointsValid = pointsNum === null || (Number.isInteger(pointsNum) && pointsNum >= 0)
  const valueNum = valueText === "" ? null : Number(valueText)
  const valueApplies = licenseType === LicenseType.Standard
  const valueValid = !valueApplies || valueNum === null || (Number.isFinite(valueNum) && valueNum >= 0)
  const commentValid = commentText.length <= 1024
  const formValid = countValid && expiryValid && licenseExpiryValid && pointsValid && valueValid && commentValid

  const handleSubmit = async () => {
    if (!formValid || !skuId) return
    setStep("generating")
    setErrorMessage(undefined)
    try {
      const { codes } = await Api.generateCodes({
        skuId,
        count: countNum,
        expirationDays: expiryNum ?? undefined,
        licenseExpiryDays: licenseExpiryNum ?? undefined,
        rewardPoints: pointsNum ?? undefined,
        licenseType,
        comment: commentText.trim() || undefined,
        skuVersionId: versionId || undefined,
        codeValueCents: valueApplies && valueNum !== null ? Math.round(valueNum * 100) : undefined,
        // only orgs with iLok on get a say; absent = the server default (enabled)
        ilokEnabled: ilokAvailable ? ilokEnabled : undefined,
        accessToken,
      })
      setCodes(codes)
      setStep("success")
      onGeneratedRef.current?.()
    } catch (err: any) {
      console.error("Failed to generate codes:", err)
      setErrorMessage(err?.message ?? "Failed to generate codes")
      setStep("error")
    }
  }

  const codesAsText = () => codes.join("\n") + "\n"

  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codesAsText())
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error("Copy failed:", err)
    }
  }

  const handleSave = () => {
    const blob = new Blob([codesAsText()], { type: "text/plain;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    const safeName = (skuName ?? skuId ?? "sku").replace(/[^a-z0-9]+/gi, "-").toLowerCase()
    a.href = url
    a.download = `redemption-codes-${safeName}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      {...backdropDismiss(() => { close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={close} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Generate Redemption Codes</h2>
        {skuName && (
          <p className="generate-codes-subtitle">for <strong>{skuName}</strong></p>
        )}

        {(step === "form" || step === "generating" || step === "error") && (
          <>
            <div className="generate-codes-form">
              <label className="generate-codes-field">
                <span className="generate-codes-label">Number of codes (1–{MAX_COUNT})</span>
                <input
                  type="number"
                  min={1}
                  max={MAX_COUNT}
                  step={1}
                  value={countText}
                  onChange={(e) => setCountText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "generating"}
                  autoFocus
                />
              </label>

              <label className="generate-codes-field">
                <span className="generate-codes-label">Redemption window (days)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={expiryText}
                  onChange={(e) => setExpiryText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "generating"}
                  placeholder="no expiry"
                />
                <span className="generate-codes-hint">How long the codes stay redeemable — empty for no limit</span>
              </label>

              <label className="generate-codes-field">
                <span className="generate-codes-label">License expiry (days)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={licenseExpiryText}
                  onChange={(e) => setLicenseExpiryText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "generating"}
                  placeholder="never"
                />
                <span className="generate-codes-hint">Duration of the license granted on redemption — leave empty for a permanent license</span>
              </label>

              <label className="generate-codes-field">
                <span className="generate-codes-label">Reward points</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={pointsText}
                  onChange={(e) => setPointsText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "generating"}
                  placeholder="none"
                />
                <span className="generate-codes-hint">Loyalty points deposited on redemption, on top of the SKU's contents</span>
              </label>

              {versions.length > 0 && (
                <label className="generate-codes-field">
                  <span className="generate-codes-label">Version</span>
                  <select
                    value={versionId}
                    onChange={(e) => setVersionId(e.target.value)}
                    className="generate-codes-input"
                    disabled={step === "generating"}
                    style={{ width: "auto" }}
                  >
                    <option value="">Latest (v{versions[0].version})</option>
                    {versions.slice(1).map((v) => (
                      <option key={v.id} value={v.id}>v{v.version}</option>
                    ))}
                  </select>
                  <span className="generate-codes-hint">
                    The version the codes grant — retroactive plug-ins of newer versions still apply at redemption
                  </span>
                </label>
              )}

              <label className="generate-codes-field">
                <span className="generate-codes-label">License type</span>
                <select
                  value={licenseType}
                  onChange={(e) => setLicenseType(e.target.value as LicenseType)}
                  className="generate-codes-input"
                  disabled={step === "generating"}
                  style={{ width: "auto" }}
                >
                  {GRANTABLE_LICENSE_TYPES.map((t) => (
                    <option key={t} value={t}>{licenseTypeLabels[t]}</option>
                  ))}
                </select>
                <span className="generate-codes-hint">The type of the licenses these codes deposit</span>
              </label>

              {ilokAvailable && (
                <label className="generate-codes-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={ilokEnabled}
                    onChange={(e) => setIlokEnabled(e.target.checked)}
                    disabled={step === "generating"}
                  />
                  <span className="generate-codes-label" style={{ margin: 0 }}>
                    Allow an iLok license
                  </span>
                  <span className="generate-codes-hint" style={{ margin: 0 }}>
                    untick for native-only codes — the redeemer can't request an iLok counterpart
                  </span>
                </label>
              )}

              {valueApplies && (
                <label className="generate-codes-field">
                  <span className="generate-codes-label">Value per code ($)</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={valueText}
                    onChange={(e) => setValueText(e.target.value)}
                    className="generate-codes-input"
                    disabled={step === "generating"}
                    placeholder={suggestion === undefined ? "suggested value" : "0.00"}
                  />
                  <span className="generate-codes-hint">
                    {suggestion === undefined
                      ? "What one code is worth — reported to iLok when a redeemed license is deposited there"
                      : suggestion.averageCents !== null
                        ? `Average of ${suggestion.orderCount} paid order${suggestion.orderCount === 1 ? "" : "s"} — reported to iLok when a redeemed license is deposited there`
                        : "No orders yet — taken from the offering price or the product values; reported to iLok when a redeemed license is deposited there"}
                  </span>
                </label>
              )}

              <label className="generate-codes-field">
                <span className="generate-codes-label">Comment</span>
                <input
                  type="text"
                  maxLength={1024}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "generating"}
                  placeholder="why this batch is being generated"
                />
              </label>
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={close} disabled={step === "generating"}>Cancel</button>
              <button
                className="btn-upgrade"
                disabled={!formValid || step === "generating"}
                onClick={handleSubmit}
              >
                {step === "generating" ? <><Spinner size={12} /> Generating…</> : "Generate"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">
              Generated <strong>{codes.length}</strong> code{codes.length === 1 ? "" : "s"}.
            </p>
            <div className="redeem-actions">
              <button onClick={handleCopy}>{copied ? "Copied!" : "Copy"}</button>
              <button onClick={handleSave}>Save</button>
              <button className="btn-upgrade" onClick={close}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
})
